import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import TenantSetupComponent from "./tenantSetup.component";
import { useAppDispatch } from "../../hooks/reduxHooks";
import {
  getTenantSetupCheckApi,
  editBusinessInfoApi,
  updateTenantTutorialApi,
} from "../../services/tenantSetup.service";
import { getPaidStatusApi } from "../../services/credit.service";
import { setCurrentPlan, setIsPaidPlan, setPlanLoading } from "../../store/slices/currentPlan.slice";
import {
  getSetupData,
  updateStepData,
  getStepData,
  clearSetupData,
} from "../../utils/tenantSetupStorage";

const TenantSetupContainer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // State management for three-step setup
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState(() => getSetupData());
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Check if tenant setup is already completed
  useEffect(() => {
    const setupCompleted = localStorage.getItem("tenantSetupCompleted");
    if (setupCompleted === "true") {
      navigate("/dashboard");
    }
  }, [navigate]);

  // Fetch plan status in tenant setup flow (header is not mounted here)
  useEffect(() => {
    dispatch(setPlanLoading(true));

    dispatch(
      getPaidStatusApi({
        finalCallback: () => {},
        successCallback: (response) => {
          let isPaid = false;
          if (typeof response === "boolean") {
            isPaid = response;
          } else if (typeof response === "object" && response !== null) {
            isPaid = Boolean(response.paid || response.isPaid || response.status || response.data || response);
          } else {
            isPaid = Boolean(response);
          }

          dispatch(setIsPaidPlan(isPaid));
          dispatch(setCurrentPlan(isPaid ? "PRO" : "TRIAL"));
          dispatch(setPlanLoading(false));
        },
        failureCallback: () => {
          dispatch(setPlanLoading(false));
        },
      })
    );
  }, [dispatch]);

  // Handle step 1 form submission - Organization Setup (save to session storage and state)
  const handleStep1Submit = (formData) => {
    updateStepData("step1", formData);
    setSetupData((prev) => ({
      ...prev,
      step1: formData,
    }));
    setCurrentStep(2);
  };

  // Handle step 2 form submission - AI Agent Setup (save to session storage and state)
  const handleStep2Submit = (formData) => {
    updateStepData("step2", formData);
    setSetupData((prev) => ({
      ...prev,
      step2: formData,
    }));
    setCurrentStep(3);
  };

  // Handle step 3 - Final setup completion with single API call
  const handleStep3Submit = () => {
    setLoading(true);
    setLoadingMessage("Submitting tenant setup...");

    // Combine all step data into the required format
    const step1Data = setupData.step1 || {};
    const step2Data = setupData.step2 || {};

    const selectedTools = Array.isArray(step2Data.intervention_tools)
      ? step2Data.intervention_tools
      : [];

    // Use selected_tools_required_info from step2 if available, otherwise build fallback
    const toolRequiredInfo = Array.isArray(step2Data.selected_tools_required_info) && step2Data.selected_tools_required_info.length > 0
      ? step2Data.selected_tools_required_info
      : selectedTools.map(toolName => {
        if (toolName === "ticket") {
          return { tool: "ticket", required_info: ["name", "contact_number"] };
        } else if (toolName === "handle_booking_order") {
          return { tool: "handle_booking_order", required_info: ["contact_number", "address", "org_email"] };
        }
        return null;
      }).filter(Boolean);

    const apiData = {
      org_name: step1Data.org_name || "",
      contact_number: step1Data.org_contact || "",
      org_email: step1Data.org_email || "",
      org_description: step1Data.org_description || "",
      agent_name: step2Data.agent_name || "AI Bot",
      language_to_reply: step2Data.language || ["Auto Detect"],
      romanize_reply: step2Data.romanize_reply || false,
      agent_goal_type: step2Data.agent_goal_type || "",
      additional_agent_goal: step2Data.additional_agent_goal || "",
      response_style: step2Data.prompt || "standard",
      selected_tools: selectedTools,
      selected_tools_required_info: toolRequiredInfo,
    };

    const apiFunction = editBusinessInfoApi;

    dispatch(
      apiFunction({
        params: apiData,
        finalCallback: () => {
          setLoading(false);
          setLoadingMessage("");
        },
        successCallback: (response) => {
          message.success("Tenant setup completed successfully!");
          clearSetupData(); // Clear session storage on successful setup
          localStorage.setItem("tenantSetupCompleted", "true");

          // Update tutorial completion status
          dispatch(
            updateTenantTutorialApi({
              status: true,
              finalCallback: () => { },
              successCallback: () => {
                localStorage.setItem("tutorialComplete", "true");
                navigate("/dashboard");
              },
              failureCallback: (error) => {
                console.error("Failed to update tutorial status:", error);
                // Still navigate even if tutorial update fails
                navigate("/dashboard");
              },
            })
          );
        },
        failureCallback: (error) => {
          message.error("Failed to complete tenant setup. Please try again.");
        },
      })
    );
  };

  // Handle navigation between steps
  const handleStepNavigation = (direction) => {
    if (direction === "next" && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else if (direction === "back" && currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <TenantSetupComponent
      currentStep={currentStep}
      setupData={setupData}
      loading={loading}
      loadingMessage={loadingMessage}
      onStep1Submit={handleStep1Submit}
      onStep2Submit={handleStep2Submit}
      onStep3Submit={handleStep3Submit}
      onStepNavigation={handleStepNavigation}
    />
  );
};

export default TenantSetupContainer;
